/**
 * #380 — Fall 2: Selbstanmeldung, wenn die Adresse unbekannt ist.
 *
 * Erreichbar über einen weitergeleiteten Link, einen QR-Code oder vom Aushang. Wir wissen
 * hier **nicht**, wer tippt — deshalb schreibt diese Seite nichts in einen Verteiler,
 * sondern fordert eine Bestätigungsmail an.
 *
 * Zwei Dinge sind bewusst so und keine Nachlässigkeit:
 *
 *   - Die Seite zeigt **nicht**, in welchen Verteilern die eingegebene Adresse schon
 *     steht. Sonst wäre sie ein Abfragedienst für jeden, der Adressen durchprobiert.
 *   - Die Antwort lautet **immer** gleich, auch bei unbekannter Adresse. Aus demselben
 *     Grund.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { MdCheck, MdError, MdMarkEmailRead } from 'react-icons/md';
import { lightTheme } from '../helfer/types';
import PublicBrandHeader, { PublicLegalFooter } from './PublicChrome';
import VerteilerAngaben, { Angaben, LEERE_ANGABEN } from './VerteilerAngaben';

interface OffenerVerteiler { id: number; name: string; description: string | null }

const theme = lightTheme;

const karte: React.CSSProperties = {
  width: '100%', maxWidth: 560, background: theme.bgCard, borderRadius: 'var(--r-l)',
  border: `1px solid ${theme.border}`, padding: 26,
};
const buehne: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh',
  padding: '24px 16px', background: theme.bg,
};

/** Auswahl treffen und Bestätigung anfordern. */
export const VerteilerSignupPage: React.FC = () => {
  const [offen, setOffen] = useState<OffenerVerteiler[]>([]);
  const [gewaehlt, setGewaehlt] = useState<Set<number>>(new Set());
  const [email, setEmail] = useState('');
  // #388: Ohne diese Angaben steht im Pool nur eine Adresse — kein Name in der
  // Anrede, und die Pflege filtert an der Person vorbei.
  const [angaben, setAngaben] = useState<Angaben>(LEERE_ANGABEN);
  const [status, setStatus] = useState<'laedt' | 'offen' | 'gesendet'>('laedt');
  const [sendet, setSendet] = useState(false);

  useEffect(() => {
    axios.get('/api/email-verteiler/oeffentlich')
      .then(r => { setOffen(r.data.data || []); setStatus('offen'); })
      .catch(() => setStatus('offen'));
  }, []);

  const umschalten = (id: number) => setGewaehlt(s => {
    const n = new Set(s);
    if (n.has(id)) { n.delete(id); } else { n.add(id); }
    return n;
  });

  // Der Name ist Pflicht: Eine Zeile ohne ihn laesst sich weder anreden noch zuordnen.
  // Der Verband bleibt freiwillig — eine erzwungene Angabe hielte Leute ab, die schlicht
  // auf dem Verteiler stehen wollen.
  const vollstaendig = () =>
    !!email.trim() && gewaehlt.size > 0 && !!angaben.firstName.trim() && !!angaben.lastName.trim();

  const anfordern = async () => {
    if (!vollstaendig() || sendet) return;
    setSendet(true);
    try {
      await axios.post('/api/email-verteiler/anmeldung', {
        email: email.trim(), verteilerIds: Array.from(gewaehlt), angaben,
      });
    } catch {
      // Die Antwort ist bewusst immer dieselbe — auch ein Fehlschlag verraet nichts.
    }
    setStatus('gesendet');
    setSendet(false);
  };

  if (status === 'laedt') {
    return (
      <div style={buehne}>
        <PublicBrandHeader />
        <div style={{ ...karte, textAlign: 'center' }}>
          <p style={{ color: theme.textMuted, margin: 0 }}>Einen Moment …</p>
        </div>
        <PublicLegalFooter />
      </div>
    );
  }

  /**
   * Leerzustand: Es ist kein Verteiler zur Selbstanmeldung freigegeben.
   *
   * Vorher blieb hier das Formular stehen — Überschrift, E-Mail-Feld, toter Knopf. Das
   * sieht nach einem Fehler aus und lässt die Person mit nichts zurück. Wer hier landet,
   * hat meistens einen Aushang oder QR-Code vor sich und kann nichts dafür, dass gerade
   * nichts offen ist. Also: sagen, was los ist, und wohin man sich wenden kann.
   */
  if (offen.length === 0) {
    return (
      <div style={buehne}>
        <PublicBrandHeader />
        <div style={{ ...karte, textAlign: 'center' }}>
          <MdMarkEmailRead size={44} color={theme.textMuted} />
          <h1 style={{ fontSize: '1.2rem', fontWeight: 700, color: theme.text, margin: '12px 0 8px' }}>
            Derzeit keine Anmeldung möglich
          </h1>
          <p style={{ color: theme.textMuted, fontSize: '.9rem', lineHeight: 1.6, margin: 0 }}>
            Zurzeit ist kein E-Mail-Verteiler zur Selbstanmeldung freigegeben. Wenn du über
            Dienste informiert werden möchtest, wende dich bitte an deine Dienstplanung —
            sie kann dich eintragen.
          </p>
        </div>
        <PublicLegalFooter />
      </div>
    );
  }

  if (status === 'gesendet') {
    return (
      <div style={buehne}>
        <PublicBrandHeader />
        <div style={{ ...karte, textAlign: 'center' }}>
          <MdMarkEmailRead size={44} color={theme.success} />
          <h1 style={{ fontSize: '1.2rem', fontWeight: 700, color: theme.text, margin: '12px 0 8px' }}>
            Schau in dein Postfach
          </h1>
          <p style={{ color: theme.textMuted, fontSize: '.9rem', lineHeight: 1.6, margin: 0 }}>
            Wenn die Adresse gültig ist, haben wir eine Bestätigung geschickt. Erst der Klick
            auf den Link darin trägt dich ein — bis dahin ist nichts gespeichert.
          </p>
        </div>
        <PublicLegalFooter />
      </div>
    );
  }

  return (
    <div style={buehne}>
      <PublicBrandHeader />
      <div style={karte}>
        <h1 style={{ fontSize: '1.2rem', fontWeight: 700, color: theme.text, margin: '0 0 4px' }}>
          Für Verteiler anmelden
        </h1>
        <p style={{ fontSize: '.86rem', color: theme.textMuted, margin: '0 0 18px', lineHeight: 1.55 }}>
          Wähle aus, worüber du informiert werden möchtest.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {offen.map(v => (
            <label
              key={v.id}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 11, padding: '12px 14px',
                borderRadius: 'var(--r-l)', cursor: 'pointer',
                border: `1px solid ${gewaehlt.has(v.id) ? theme.accent : theme.border}`,
                background: gewaehlt.has(v.id) ? `${theme.accent}0f` : theme.bgCard,
              }}
            >
              <input
                type="checkbox"
                checked={gewaehlt.has(v.id)}
                onChange={() => umschalten(v.id)}
                style={{ marginTop: 2, accentColor: theme.accent, width: 16, height: 16 }}
              />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: '.9rem', fontWeight: 600, color: theme.text }}>{v.name}</span>
                {v.description && (
                  <span style={{ fontSize: '.79rem', color: theme.textMuted, lineHeight: 1.45 }}>{v.description}</span>
                )}
              </span>
            </label>
          ))}
        </div>

        <div style={{ marginBottom: 14 }}>
          <VerteilerAngaben werte={angaben} onChange={setAngaben} />
        </div>

        <label style={{ display: 'block', fontSize: '.78rem', color: theme.textMuted, marginBottom: 5 }}>
          E-Mail-Adresse
        </label>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="deine@adresse.de"
          style={{
            width: '100%', padding: '11px 13px', borderRadius: 'var(--r-m)', fontSize: '.9rem',
            border: `1px solid ${theme.border}`, background: theme.bgInput, color: theme.text,
            fontFamily: 'inherit', boxSizing: 'border-box',
          }}
        />

        <p style={{ fontSize: '.8rem', color: theme.textMuted, margin: '12px 0 0', lineHeight: 1.6 }}>
          Wir schicken dir eine E-Mail mit einem Bestätigungslink. Erst wenn du ihn anklickst,
          bist du eingetragen.
        </p>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button
            type="button"
            onClick={anfordern}
            disabled={!vollstaendig() || sendet}
            style={{
              padding: '10px 18px', borderRadius: 'var(--r-m)', border: 'none', fontSize: '.9rem',
              fontWeight: 600, fontFamily: 'inherit', color: '#fff',
              cursor: (!vollstaendig() || sendet) ? 'default' : 'pointer',
              background: (!vollstaendig() || sendet) ? theme.border : theme.accent,
            }}
          >
            Bestätigung anfordern
          </button>
        </div>
      </div>
      <PublicLegalFooter />
    </div>
  );
};

/**
 * Bestätigungsklick. Danach geht es direkt auf die Einstellungsseite weiter — dort kann
 * die Person gleich weiterwählen, statt auf einer Sackgasse zu landen.
 */
export const VerteilerConfirmPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [fehler, setFehler] = useState(token ? '' : 'Dieser Link ist unvollständig.');
  const [fertig, setFertig] = useState(false);
  const [laeuft, setLaeuft] = useState(false);

  // #503: erst auf Knopfdruck — Mail-Scanner öffnen Links vorab (und führen manchmal JavaScript aus)
  const bestaetigen = () => {
    if (!token) return;
    setLaeuft(true);
    axios.post(`/api/email-verteiler/anmeldung/${token}`)
      .then(r => {
        setFertig(true);
        const t = r.data?.data?.token;
        if (t) setTimeout(() => navigate(`/verteiler/${t}`, { replace: true }), 1600);
      })
      .catch(e => setFehler(e?.response?.data?.error || 'Dieser Link ist ungültig oder wurde bereits verwendet.'))
      .finally(() => setLaeuft(false));
  };

  return (
    <div style={buehne}>
      <PublicBrandHeader />
      <div style={{ ...karte, textAlign: 'center' }}>
        {!fehler && !fertig && (
          <>
            <h1 style={{ fontSize: '1.2rem', fontWeight: 700, color: theme.text, margin: '0 0 8px' }}>
              Anmeldung zum Verteiler bestätigen
            </h1>
            <p style={{ color: theme.textMuted, fontSize: '.9rem', lineHeight: 1.6, margin: '0 0 18px' }}>
              Ein Klick noch — dann bekommst du die Rundmails dieses Verteilers.
            </p>
            <button type="button" onClick={bestaetigen} disabled={laeuft}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', minHeight: 44, borderRadius: 'var(--r-m)', border: 'none', background: theme.accent, color: '#fff', fontSize: '.9rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', opacity: laeuft ? 0.7 : 1 }}>
              <MdCheck size={18} /> {laeuft ? 'Einen Moment …' : 'Anmeldung bestätigen'}
            </button>
          </>
        )}
        {fertig && (
          <>
            <MdCheck size={44} color={theme.success} />
            <h1 style={{ fontSize: '1.2rem', fontWeight: 700, color: theme.text, margin: '12px 0 8px' }}>
              Angemeldet
            </h1>
            <p style={{ color: theme.textMuted, fontSize: '.9rem', lineHeight: 1.6, margin: 0 }}>
              Jede E-Mail, die du von uns bekommst, trägt unten einen Link zum Abmelden.
            </p>
          </>
        )}
        {fehler && (
          <>
            <MdError size={44} color={theme.danger} />
            <h1 style={{ fontSize: '1.2rem', fontWeight: 700, color: theme.text, margin: '12px 0 8px' }}>
              Das hat nicht geklappt
            </h1>
            <p style={{ color: theme.textMuted, fontSize: '.9rem', lineHeight: 1.6, margin: 0 }}>{fehler}</p>
          </>
        )}
      </div>
      <PublicLegalFooter />
    </div>
  );
};

export default VerteilerSignupPage;
